<template>
  <div class="weekly-drawer-root">
    <div
      class="weekly-backdrop"
      :class="{ 'is-open': open }"
      @click="$emit('close')"
    />

    <aside
      class="weekly-drawer"
      :class="{ 'is-open': open }"
      role="dialog"
      aria-modal="false"
      :aria-label="cinema?.cinemaName || '劇場週次詳細'"
    >
      <header class="weekly-header">
        <button type="button" class="weekly-close" @click="$emit('close')">
          <i class="fas fa-arrow-right" aria-hidden="true" />
        </button>
        <div class="weekly-title">
          <p class="weekly-title__name" :title="cinema?.cinemaName">{{ cinema?.cinemaName || '-' }}</p>
          <p class="weekly-title__sub">{{ cinema?.prefectureName }}</p>
        </div>
      </header>

      <!-- 公開週セレクタ：Week1 は公開日を含む金曜〜木曜。公開前の放映がある場合は「公開前N週」も並ぶ -->
      <div class="weekly-weeks">
        <button
          type="button"
          class="weekly-nav"
          :disabled="!canPrevWeek"
          aria-label="前の週"
          @click="stepWeek(-1)"
        >
          <i class="fas fa-chevron-left" aria-hidden="true" />
        </button>
        <div class="weekly-weeks__scroll">
          <button
            v-for="n in weekNoList"
            :key="`week-${n}`"
            type="button"
            class="weekly-week"
            :class="{ 'is-active': n === weekNo }"
            @click="selectWeek(n)"
          >
            {{ formatWeekLabel(n) }}
          </button>
        </div>
        <button
          type="button"
          class="weekly-nav"
          :disabled="!canNextWeek"
          aria-label="次の週"
          @click="stepWeek(1)"
        >
          <i class="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <div class="weekly-body">
        <div v-if="loading" class="weekly-state weekly-state--loading">
          <i class="fas fa-spinner fa-spin" aria-hidden="true" /> 読み込み中…
        </div>

        <div v-else-if="errorMessage" class="weekly-state">
          <p class="weekly-state__title">データを取得できませんでした</p>
          <button type="button" class="weekly-retry" @click="reload">再読み込み</button>
        </div>

        <template v-else-if="detail">
          <p class="weekly-range">{{ weekRangeText }}</p>

          <!-- 週合計 -->
          <div class="weekly-summary">
            <div class="weekly-summary__item">
              <span class="weekly-summary__label">週興行収入</span>
              <span class="weekly-summary__value">
                {{ summary.boxOffice }}<span class="weekly-summary__unit">{{ summary.boxOfficeUnit }}</span>
              </span>
            </div>
            <div class="weekly-summary__item">
              <span class="weekly-summary__label">週入場者数</span>
              <span class="weekly-summary__value">
                {{ summary.occupiedSeats }}<span class="weekly-summary__unit">人</span>
              </span>
            </div>
            <div class="weekly-summary__item">
              <span class="weekly-summary__label">週充足率</span>
              <span class="weekly-summary__value">{{ summary.occupancyRate }}</span>
            </div>
          </div>

          <!--
            2本のグラフは軸を共有せず上下に並べ、日付列だけを揃える。
            2軸を1つのグラフに重ねると、どちらの軸がどちらの系列なのか読み取れず誤読を招くため。
          -->
          <section class="weekly-chart">
            <h4 class="weekly-chart__title">
              興行収入 <span class="weekly-chart__unit">（円）</span>
            </h4>
            <div class="weekly-chart__bars">
              <div v-for="day in dailyList" :key="`box-${day.date}`" class="weekly-bar">
                <span class="weekly-bar__value">{{ day.boxOfficeText }}</span>
                <div class="weekly-bar__track">
                  <div
                    class="weekly-bar__fill weekly-bar__fill--box"
                    :class="{ 'is-today': day.isToday }"
                    :style="{ height: `${day.boxOfficeHeight}%` }"
                  />
                </div>
              </div>
            </div>
          </section>

          <section class="weekly-chart">
            <h4 class="weekly-chart__title">
              入場者数 <span class="weekly-chart__unit">（人）</span>
            </h4>
            <div class="weekly-chart__bars">
              <div v-for="day in dailyList" :key="`occ-${day.date}`" class="weekly-bar">
                <span class="weekly-bar__value">{{ day.occupiedSeatsText }}</span>
                <div class="weekly-bar__track">
                  <div
                    class="weekly-bar__fill weekly-bar__fill--occ"
                    :class="{ 'is-today': day.isToday }"
                    :style="{ height: `${day.occupiedSeatsHeight}%` }"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- 共通の X 軸ラベル。2つのグラフが同じ日付列を指していることを明示する -->
          <div class="weekly-axis">
            <div
              v-for="day in dailyList"
              :key="`axis-${day.date}`"
              class="weekly-axis__item"
              :class="{ 'is-weekend': day.isWeekend, 'is-today': day.isToday }"
            >
              <span class="weekly-axis__dow">{{ day.dayOfWeek }}</span>
              <span class="weekly-axis__date">{{ day.dateLabel }}</span>
            </div>
          </div>

          <!-- グラフの内容をテキストでも読めるようにする（アクセシビリティ要件） -->
          <table class="weekly-table">
            <caption class="weekly-table__caption">日別内訳</caption>
            <thead>
              <tr>
                <th>日付</th>
                <th>上映</th>
                <th>座席</th>
                <th>入場者</th>
                <th>充足率</th>
                <th>興行収入</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="day in dailyList"
                :key="`row-${day.date}`"
                :class="{ 'is-today': day.isToday }"
              >
                <th scope="row">{{ day.dateLabel }}（{{ day.dayOfWeek }}）</th>
                <td>{{ formatCount(day.scheduleCount) }}</td>
                <td>{{ formatCount(day.seatTotal) }}</td>
                <td>{{ day.occupiedSeatsText }}</td>
                <td>{{ formatRate(day.occupancyRate) }}</td>
                <td>{{ day.boxOfficeText }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { getCinemaWeeklyDetailService } from '@/api/dp/index.js'
import {
  buildWeekNoList,
  formatCount,
  formatRate,
  formatWeekLabel,
  formatWeekRange,
  mapWeeklyDaily,
  mapWeeklySummary,
} from '@/utils/dashboard-cinema-rank.js'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  /** 選択中の劇場（cinema-content の行モデル） */
  cinema: {
    type: Object,
    default: null,
  },
  movieId: {
    type: Number,
    default: null,
  },
  /**
   * ランキングと同一の上映バージョン条件。
   * 揃えないとランキングの数字と週次の数字が一致せず、不具合に見えてしまう。
   */
  versionParams: {
    type: Object,
    default: () => ({}),
  },
})
defineEmits(['close'])

const detail = ref(null)
const weekNo = ref(null)
/** 選択可能な最小の週（公開前の放映があれば 0 以下、なければ 1） */
const minWeekNo = ref(1)
const maxWeekNo = ref(0)
const loading = ref(false)
const errorMessage = ref('')

let fetchId = 0

const dailyList = computed(() => mapWeeklyDaily(detail.value?.dailyList))
const summary = computed(() => mapWeeklySummary(detail.value))
const weekRangeText = computed(() =>
  detail.value ? formatWeekRange(detail.value.weekStart, detail.value.weekEnd) : '')

const weekNoList = computed(() => buildWeekNoList(minWeekNo.value, maxWeekNo.value))
const canPrevWeek = computed(() => weekNo.value != null && weekNo.value > minWeekNo.value)
const canNextWeek = computed(() => (weekNo.value ?? 0) < maxWeekNo.value)

async function fetchDetail(targetWeekNo) {
  if (!props.movieId || !props.cinema?.cinemaId) return
  const currentFetchId = ++fetchId
  loading.value = true
  errorMessage.value = ''

  try {
    const res = await getCinemaWeeklyDetailService({
      movieId: props.movieId,
      cinemaId: props.cinema.cinemaId,
      weekNo: targetWeekNo ?? undefined,
      ...props.versionParams,
    })
    if (currentFetchId !== fetchId) return
    detail.value = res
    weekNo.value = res?.weekNo ?? null
    // 旧レスポンス（minWeekNo なし）は公開前の週が無い扱い
    minWeekNo.value = res?.minWeekNo ?? 1
    maxWeekNo.value = res?.maxWeekNo ?? 0
  } catch (error) {
    if (currentFetchId !== fetchId) return
    detail.value = null
    errorMessage.value = String(error?.message || error)
    console.error('[dashboard] getCinemaWeeklyDetailService failed', error)
  } finally {
    if (currentFetchId === fetchId) loading.value = false
  }
}

function reload() {
  void fetchDetail(weekNo.value)
}

function selectWeek(n) {
  if (n === weekNo.value) return
  void fetchDetail(n)
}

function stepWeek(delta) {
  if (weekNo.value == null) return
  const next = weekNo.value + delta
  if (next < minWeekNo.value || next > maxWeekNo.value) return
  void fetchDetail(next)
}

// 劇場・作品・バージョン条件が変わったら、週番号は未指定で取り直す
// （今日（JST）の属する公開週がバックエンド側で選ばれる。DEV-14945 以前は「最新データのある週」だった）
watch(
  () => [props.open, props.cinema?.cinemaId, props.movieId, props.versionParams],
  ([isOpen]) => {
    if (!isOpen) return
    detail.value = null
    weekNo.value = null
    minWeekNo.value = 1
    maxWeekNo.value = 0
    void fetchDetail(null)
  },
  { deep: true },
)
</script>

<style lang="less" scoped>
.weekly-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1900;
  background: color-mix(in srgb, #000 28%, transparent);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;

  &.is-open {
    opacity: 1;
    pointer-events: auto;
  }
}

.weekly-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1910;
  display: flex;
  flex-direction: column;
  width: min(460px, 92vw);
  background: var(--mc-bg, #fff);
  border-left: 1px solid var(--mc-border, #e5e5e5);
  box-shadow: -8px 0 28px rgb(0 0 0 / 14%);
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);

  &.is-open {
    transform: translateX(0);
  }
}

.weekly-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 12px 14px;
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
  background: var(--mc-bg-soft, #fafafa);
}

.weekly-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 15px;
  background: var(--mc-bg, #fff);
  color: var(--mc-text, #000);
  cursor: pointer;

  &:hover {
    border-color: var(--mc-accent, #f9da49);
  }
}

.weekly-title {
  min-width: 0;
}

/* 劇場名＋都道府県はランキング表（.cinema-name / .cinema-pref）と同じ詰まり方に揃える。
   line-height を明示しないと親から継承した大きい行高で 2 行が離れて見える */
.weekly-title__name {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mc-text, #000);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.weekly-title__sub {
  margin: 0;
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 11px;
  line-height: 1.3;
}

/* ---------- 週セレクタ ---------- */
.weekly-weeks {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 10px;
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
}

.weekly-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 13px;
  background: var(--mc-bg, #fff);
  color: var(--mc-text, #000);
  font-size: 11px;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.weekly-weeks__scroll {
  display: flex;
  gap: 6px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.weekly-week {
  /* 高さ固定のボタン。flex 中央寄せ＋line-height:1 でラベルを天地左右中央に置く。
     角丸は weekly-summary__item と揃えて、ピル型ではなく控えめな角丸にする */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 64px;
  height: 26px;
  padding: 0 12px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 8px;
  background: var(--mc-bg, #fff);
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;

  &.is-active {
    border-color: var(--mc-accent, #f9da49);
    background: var(--mc-accent, #f9da49);
    color: #1c1c1c;
    font-weight: 700;
  }
}

/* ---------- 本体 ---------- */
.weekly-body {
  flex: 1;
  min-height: 0;
  padding: 12px 14px 20px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.weekly-state {
  padding: 40px 0;
  text-align: center;
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 12px;
}

/* 読み込み中の文字色を cinema-box__num と揃えて赤にする */
.weekly-state--loading {
  color: var(--mc-accent-strong, #a31d15);
}

.weekly-state__title {
  margin: 0 0 10px;
  font-size: 13px;
}

.weekly-retry {
  height: 28px;
  padding: 0 16px;
  border: 1px solid var(--mc-accent, #f9da49);
  border-radius: 14px;
  background: transparent;
  color: var(--mc-text, #000);
  font-size: 12px;
  cursor: pointer;
}

.weekly-range {
  margin: 0 0 10px;
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 12px;
  text-align: center;
}

.weekly-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.weekly-summary__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 8px;
  background: var(--mc-bg-soft, #fafafa);
  text-align: center;
}

.weekly-summary__label {
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 10px;
}

.weekly-summary__value {
  color: var(--mc-text, #000);
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.weekly-summary__unit {
  margin-left: 1px;
  font-size: 10px;
  font-weight: 500;
}

/* ---------- グラフ ---------- */
.weekly-chart {
  margin-bottom: 14px;
}

.weekly-chart__title {
  margin: 0 0 6px;
  color: var(--mc-text, #000);
  font-size: 12px;
  font-weight: 700;
}

.weekly-chart__unit {
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 10px;
  font-weight: 500;
}

.weekly-chart__bars {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  height: 96px;
}

.weekly-bar {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 3px;
  min-width: 0;
}

.weekly-bar__value {
  color: var(--mc-text-muted, #6a6a6a);
  font-size: 9px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

.weekly-bar__track {
  display: flex;
  align-items: flex-end;
  height: 72px;
}

.weekly-bar__fill {
  width: 100%;
  min-height: 2px;
  border-radius: 3px 3px 0 0;
  transition: height 0.3s ease;

  /* 今日はテーマ主色で強調（色だけに頼らず、下の軸ラベルでも太字にする） */
  &.is-today {
    outline: 1px solid var(--mc-accent, #f9da49);
    outline-offset: 1px;
  }
}

.weekly-bar__fill--box {
  background: var(--mc-accent-strong, #a31d15);
}

.weekly-bar__fill--occ {
  background: color-mix(in srgb, var(--mc-accent, #f9da49) 70%, #7a8ba6);
}

.weekly-axis {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  padding-top: 6px;
  border-top: 1px solid var(--mc-border, #e5e5e5);
}

.weekly-axis__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  color: var(--mc-text-muted, #6a6a6a);
  font-size: 10px;

  &.is-weekend {
    color: var(--mc-accent-strong, #a31d15);
  }

  &.is-today {
    font-weight: 700;
    color: var(--mc-text, #000);
  }
}

.weekly-axis__dow {
  font-weight: 600;
}

.weekly-axis__date {
  font-size: 9px;
  opacity: 0.75;
}

/* ---------- テキスト表 ---------- */
.weekly-table {
  width: 100%;
  margin-top: 16px;
  border-collapse: collapse;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.weekly-table__caption {
  margin-bottom: 6px;
  color: var(--mc-text, #000);
  font-size: 12px;
  font-weight: 700;
  text-align: left;
}

.weekly-table th,
.weekly-table td {
  padding: 5px 4px;
  border-bottom: 1px solid var(--mc-border, #eee);
  text-align: right;
  white-space: nowrap;
}

.weekly-table thead th {
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 10px;
  font-weight: 600;
}

.weekly-table tbody th {
  text-align: left;
  font-weight: 500;
  color: var(--mc-text, #000);
}

.weekly-table tbody tr.is-today {
  background: var(--mc-row-selected, #fdf3c9);
}


@media (max-width: 768px) {
  .weekly-drawer {
    width: 100vw;
  }

  .weekly-summary__value {
    font-size: 14px;
  }
}
</style>
